import { Link } from "react-router-dom";
import { Mail, Globe, Linkedin, Facebook, Instagram } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../data/content";

const SOCIAL_LINKS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/vaskiontech/", Icon: Linkedin, testid: "footer-social-linkedin" },
  { label: "Facebook", href: "https://www.facebook.com/vaskiontech", Icon: Facebook, testid: "footer-social-facebook" },
  { label: "Instagram", href: "https://www.instagram.com/vaskiontech/", Icon: Instagram, testid: "footer-social-instagram" },
];

const Footer = () => {
  const explore = NAV_LINKS.filter((l) =>
    ["Services", "Solutions", "Technologies", "Industries"].includes(l.label),
  );
  const company = NAV_LINKS.filter((l) => ["About", "Careers", "Contact"].includes(l.label));

  return (
    <footer className="bg-brand-graphite text-neutral-400" data-testid="site-footer">
      <div className="container-site grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="inline-block bg-white p-3">
            <img
              src="/assets/vaskion-logo.png"
              alt={COMPANY.name}
              className="h-12 w-auto"
              width="138"
              height="48"
            />
          </div>
          <p className="mt-5 font-heading text-sm font-medium tracking-wide text-white">
            {COMPANY.name}
          </p>
          <p className="mt-1 text-sm">{COMPANY.tagline}</p>
        </div>

        <nav aria-label="Explore" data-testid="footer-explore-nav">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-white">
            Explore
          </h3>
          <ul className="mt-5 space-y-3">
            {explore.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-sm transition-colors duration-200 hover:text-white"
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Company" data-testid="footer-company-nav">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-white">
            Company
          </h3>
          <ul className="mt-5 space-y-3">
            {company.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-sm transition-colors duration-200 hover:text-white"
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-white">
            Contact
          </h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a
                href={`mailto:${COMPANY.email}`}
                className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-white"
                data-testid="footer-email-link"
              >
                <Mail className="h-4 w-4 text-brand-red" aria-hidden="true" />
                {COMPANY.email}
              </a>
            </li>
            <li>
              <a
                href={COMPANY.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-white"
                data-testid="footer-website-link"
              >
                <Globe className="h-4 w-4 text-brand-red" aria-hidden="true" />
                {COMPANY.website}
              </a>
            </li>
          </ul>
          <div className="mt-8">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-white">
              Follow Us
            </h3>
            <ul className="mt-4 flex items-center gap-3" data-testid="footer-social-links">
              {SOCIAL_LINKS.map(({ label, href, Icon, testid }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${COMPANY.name} on ${label} (opens in a new tab)`}
                    title={`${label} — ${COMPANY.name}`}
                    className="flex h-10 w-10 items-center justify-center border border-neutral-700 text-neutral-400 transition-colors duration-200 hover:border-brand-red hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 focus-visible:ring-offset-brand-graphite"
                    data-testid={testid}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-neutral-800">
        <div className="container-site flex flex-col items-start justify-between gap-4 py-6 sm:flex-row sm:items-center">
          <p className="text-xs" data-testid="footer-copyright">
            © 2026 {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy-policy"
              className="text-xs transition-colors duration-200 hover:text-white"
              data-testid="footer-privacy-policy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-use"
              className="text-xs transition-colors duration-200 hover:text-white"
              data-testid="footer-terms-of-use-link"
            >
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
